import type { EventBus } from "../core/EventBus.ts";
import type { PathRegistry } from "../core/PathRegistry.ts";
import type { Enemy } from "../entities/Enemy.ts";
import type { Tower } from "../entities/Tower.ts";
import type {
  EnemyConfig,
  EnemyDataFile,
  WaveDataFile,
} from "../entities/types.ts";
import type { Vec2 } from "../core/types.ts";
import type { ObjectPool } from "../utils/ObjectPool.ts";
import {
  ENDLESS,
  FINAL_WAVE,
  GAME,
  SHIELD_START_WAVE,
  MUTATION_START_WAVE,
} from "../core/config.ts";
import { createRng } from "../core/Rng.ts";
import type { Rng } from "../core/Rng.ts";
import type { ThreatProfile } from "./ThreatEffects.ts";
import { applyShield } from "./shield.ts";
import { ENEMY_BEHAVIORS } from "./EnemyBehaviors.ts";
import type { EnemyBehaviorContext } from "./EnemyBehaviors.ts";
import { EnemyDirector } from "./EnemyDirector.ts";
import { burstPandemicSpores } from "./VirusInfect.ts";

/**
 * S141 / BL-124 / D-236 — Lever 1: the per-wave ECHELON mutation (elite
 * modifier). Rolled once per non-boss wave from W13, seeded + telegraphed +
 * never back-to-back. Reads as "her signal mutates the thread" (STORY §3).
 */
export type WaveModifier = "hardened" | "swift" | "veiled" | "splitting";
const MODIFIER_POOL: WaveModifier[] = [
  "hardened",
  "swift",
  "veiled",
  "splitting",
];

/** B7 (spec `2026-time-warp-softlock`) — how long an enemy may make ZERO
 *  progress before the watchdog removes it.
 *
 *  Generous on purpose. Legitimate stillness exists: a time-warp freeze lasts
 *  5 s, the breach cinematic pauses for 2.5 s, and a jammer/slow field can
 *  crawl an enemy to a near-standstill. 30 s of *sim* time is far past all of
 *  them, so this can only fire on a genuine dead end — it is a softlock
 *  backstop, not a gameplay timer.
 *
 *  NOTE it counts sim-ms, so 2x speed reaches it in half the wall-clock time,
 *  which is correct: the enemy has had the same number of simulated chances to
 *  move. */
const STUCK_TIMEOUT_MS = 30_000;

/** B7 (spec `2026-time-warp-softlock`) — the WAVE-LEVEL deadlock backstop.
 *
 *  The stuck-enemy watchdog above closes one clause of `checkWaveCompletion`
 *  (`active.length` never reaching 0). This closes the OTHER: a wave whose
 *  spawn queue never advances. Between them, no wave can stay open forever
 *  regardless of WHICH mechanism stalled it — including a fourth route nobody
 *  has found yet, which is the point. Two occurrences of this softlock both
 *  had a cause we had not predicted.
 *
 *  Sized from the data, not guessed. The longest legitimate no-progress window
 *  is `groupDelay` 3000 ms (the largest authored in waves.json) + a time-warp
 *  freeze 5000 ms + a breach cinematic 2500 ms ≈ 10.5 s if they stack. 45 s is
 *  over four times that, so it cannot fire on a slow wave — only on one that
 *  has genuinely stopped. */
const WAVE_STALL_TIMEOUT_MS = 45_000;

/** Probability a non-boss wave rolls a mutation (the steered HARD ramp).
 *  BL-137 / D-242 (S145): added the W5–10 GENTLE tier ahead of S141's W11+
 *  ramp, now that {@link MUTATION_START_WAVE} pulled to W5. ≈ 0.40 means a
 *  player sees a mutation roughly every other wave in the early-mid game —
 *  enough to break the AFK rhythm without overwhelming an Act-I board. */
function mutationRate(wave: number): number {
  if (wave <= 10) return 0.4; // Act I closing — her signal probes
  if (wave <= 17) return 0.6; // Act II opens (was 0.5; +0.1 to keep the slope monotone)
  if (wave <= 24) return 0.8; // Act II deepens (was 0.75)
  return 1.0; // Act III — Dominion: every non-boss wave is hers
}

export class WaveManager {
  private waveData: WaveDataFile;
  private enemyData: EnemyDataFile;
  private pathPixels_: Vec2[];
  private bus: EventBus;

  private currentWave_ = 0;
  private waveActive_ = false;
  private spawnQueue: { type: string; delay: number }[] = [];
  private spawnIndex = 0;
  private spawnTimer = 0;
  private enemyPool: ObjectPool<Enemy>;
  private scaling: WaveDataFile["scaling"];
  private leaksThisWave_ = 0;
  /** B7 — wave-level deadlock backstop: sim-ms since anything last changed. */
  private waveStallMs_ = 0;
  /** B7 — the progress fingerprint the stall counter compares against. */
  private waveSignature_ = "";
  private pathRegistry_: PathRegistry | null = null;
  /**
   * The run's threat profile (chain step A, 2026-08-18) — enemy HP/speed
   * multipliers per threat level, set ONCE per run via
   * {@link setThreatProfile} (from `applyThreatLevelToSystems`, `startRun()`).
   * The default IS the containment 1.00 anchor: a WaveManager that never gets
   * a profile (every pre-contract test, the golden masters) simulates
   * byte-identically to before the contract existed — `x * 1.0 === x` exactly
   * (IEEE 754). The literal (not THREAT_PROFILES.containment) keeps runtime
   * module edges acyclic: ThreatEffects imports this class type-only.
   */
  private threatProfile_: ThreatProfile = { enemyHpMult: 1, enemySpeedMult: 1 };
  private readonly shieldSeed: number;
  private shieldRng: Rng;
  private shieldAnnounced_ = false;
  // S141 / D-236 — Lever 1: the isolated mutation RNG stream + current/last roll.
  private readonly modifierSeed: number;
  private modifierRng: Rng;
  /** AC-10 / OQ-3 — the breach-ROUTING draws. Seeded from the run's EXISTING
   *  isolated breach stream seed (`rng.seed ^ BREACH_SEED_SALT`), so this adds
   *  no new RNG stream and no new determinism surface.
   *
   *  It deliberately takes the breach SEED rather than sharing BreachSystem's
   *  live cursor: routing draws happen per spawn, so a shared cursor would
   *  interleave with the eruption rolls and shift `shouldBossBreach`'s results
   *  as a side effect of unrelated spawn traffic. Re-seeding per wave (below)
   *  also makes routing independent of how many spawns preceded it within the
   *  run, so a wave routes identically whether or not earlier waves erupted. */
  private readonly breachSeed: number;
  private breachRoutingRng: Rng;
  private currentModifier_: WaveModifier | null = null;
  private lastModifier_: WaveModifier | null = null;
  // BL-156 / D-254 (S157) — the ENEMY BRAIN: the central, data-driven launch
  // DIRECTOR. It owns the launch DECISIONS — the data-driven roster + the
  // introduction schedule + the once-per-run "NEW THREAT DETECTED" telegraph
  // (lifting the hardcoded `NEW_THREAT_TYPES` allowlist into `enemies.json`
  // `special.newThreat`) + the documented composition/role model. The
  // WaveManager keeps the low-level spawn-tick / RNG plumbing and delegates the
  // introduction decision to the Brain. See `docs/design/ENEMY_BRAIN.md`.
  private readonly director: EnemyDirector;
  // BL-151 / D-250 — the ENEMY_BEHAVIORS context: the event bus + the
  // `spawnAdd` seam onto spawnEnemy(type, true). Built once; the registry
  // handlers (spawnAdds / finalBoss) are invoked from tickEnemies with it.
  // BL-152 (S154): also carries the live tower + enemy lists for the Jammer /
  // Overseer hooks (stable array refs).
  private readonly behaviorCtx: EnemyBehaviorContext;

  constructor(
    waveData: WaveDataFile,
    enemyData: EnemyDataFile,
    pathPixels: Vec2[],
    bus: EventBus,
    enemyPool: ObjectPool<Enemy>,
    shieldSeed: number,
    modifierSeed = 0,
    // BL-151 / D-250 — the isolated ENEMY_SEED_SALT stream (from createSystems),
    // exposed on the behavior context for future rolled behaviours. Defaults to
    // a no-op for bare unit harnesses; UNCONSUMED by the 3 original handlers.
    enemyRng: () => number = () => 0,
    // BL-152 (S154) — the live tower list (gs.towers) for the Jammer's
    // tower-suppression hook. Defaults to [] so bare unit harnesses (and the
    // pre-S154 call sites) construct unchanged; the Jammer hook is then a no-op.
    towers: Tower[] = [],
    // AC-10 / OQ-3 (Spec B) — the run's isolated breach-stream seed
    // (`rng.seed ^ BREACH_SEED_SALT`, from createSystems). Defaults to 0 so
    // every existing call site constructs unchanged; with the default the
    // routing lever is inert anyway, since `breachRoutedSpawnFraction`
    // defaults to 0.
    breachSeed = 0
  ) {
    this.waveData = waveData;
    this.enemyData = enemyData;
    this.pathPixels_ = pathPixels;
    this.bus = bus;
    this.enemyPool = enemyPool;
    this.scaling = waveData.scaling;
    this.shieldSeed = shieldSeed;
    this.shieldRng = createRng(this.shieldSeed);
    this.modifierSeed = modifierSeed;
    this.modifierRng = createRng(this.modifierSeed);
    this.breachSeed = breachSeed;
    this.breachRoutingRng = createRng(this.breachSeed);
    // BL-156 (S157): the Enemy Brain. Constructed from the data + bus this
    // manager already holds (it has no dependency back on the WaveManager), so
    // every existing call site builds unchanged.
    this.director = new EnemyDirector(waveData, enemyData, bus);
    // BL-151: the registry handler context. `spawnAdd` routes through the
    // private spawnEnemy with isAdd=true (the "internal add, don't re-mutate"
    // flag the old inline blocks used).
    this.behaviorCtx = {
      bus: this.bus,
      spawnAdd: (type) => this.spawnEnemy(type, true),
      enemyRng,
      // BL-152 (S154): the Jammer reads `towers`, the Overseer reads `enemies`.
      // Both are STABLE array refs (gs.towers + the pool's active list), so the
      // hooks always see the live board without a per-tick rebuild.
      towers,
      enemies: enemyPool.active,
    };
  }

  /** S141 / D-236 — the mutation active for the in-progress wave (or null). */
  get currentModifier(): WaveModifier | null {
    return this.currentModifier_;
  }

  get currentWave(): number {
    return this.currentWave_;
  }
  get waveActive(): boolean {
    return this.waveActive_;
  }
  /**
   * BL-56b — waves CLEARED so far (the live wave-progress read, vs
   * {@link FINAL_WAVE} total). While a wave is mid-fight it isn't cleared yet,
   * so the in-progress wave is excluded; once it completes (`waveActive_` →
   * false) the count ticks up. O(1), render-only — the sim never reads this.
   */
  get wavesCleared(): number {
    const cleared = this.currentWave_ - (this.waveActive_ ? 1 : 0);
    return cleared < 0 ? 0 : cleared;
  }
  get enemies(): Enemy[] {
    return this.enemyPool.active;
  }

  set pathPixels(path: Vec2[]) {
    this.pathPixels_ = path;
  }

  /** CR-5 (P22): Set path registry for on-demand secondary path reads (LD-22-12) */
  setPathRegistry(registry: PathRegistry): void {
    this.pathRegistry_ = registry;
  }

  /**
   * Chain step A (2026-08-18): install the run's threat profile. Called once
   * per run from `applyThreatLevelToSystems` (`startRun()` → BEFORE operator/
   * module effects). Deliberately NOT cleared in `reset()` — the profile
   * belongs to the run SETUP, and every run-start path sets it explicitly, so
   * a restart on a different level can never inherit stale difficulty while
   * bare harnesses (which never call this) keep the neutral anchor.
   */
  setThreatProfile(profile: ThreatProfile): void {
    this.threatProfile_ = profile;
  }

  /** The active threat profile (tests/diagnostics; neutral anchor default). */
  get threatProfile(): ThreatProfile {
    return this.threatProfile_;
  }

  reset(): void {
    this.currentWave_ = 0;
    this.waveActive_ = false;
    this.spawnQueue = [];
    this.spawnIndex = 0;
    this.spawnTimer = 0;
    this.leaksThisWave_ = 0;
    this.enemyPool.releaseAll();
    this.shieldRng = createRng(this.shieldSeed);
    this.shieldAnnounced_ = false;
    this.modifierRng = createRng(this.modifierSeed);
    this.currentModifier_ = null;
    this.lastModifier_ = null;
    this.director.reset();
  }

  startWave(): void {
    if (this.waveActive_) return;
    this.currentWave_++;
    this.waveActive_ = true;
    // AC-10 / OQ-3 — re-seed the breach-ROUTING draws per wave from the run's
    // breach seed. Mixing the wave number in (golden-ratio odd constant, the
    // standard 32-bit avalanche multiplier) keeps consecutive waves from
    // sharing a draw prefix, and makes a wave's routing depend only on
    // (runSeed, wave) — never on how many spawns or eruptions preceded it.
    this.breachRoutingRng = createRng(
      (this.breachSeed ^ Math.imul(this.currentWave_, 0x9e3779b1)) | 0
    );
    this.waveStallMs_ = 0;
    this.waveSignature_ = "";
    this.spawnQueue = this.buildSpawnQueue();
    this.spawnIndex = 0;
    this.spawnTimer = 0;
    this.leaksThisWave_ = 0;

    // CR-27: Telegraph boss waves
    const isBossWave =
      this.currentWave_ === FINAL_WAVE ||
      this.spawnQueue.some((e) => e.type === "boss");
    if (isBossWave) {
      this.bus.emit("boss.wave.incoming", { wave: this.currentWave_ });
    }

    // S141 / D-236 — Lever 1: roll the ECHELON mutation for this wave (seeded,
    // never on boss waves, telegraphed before the wave fights). Fair-difficulty
    // law: the banner names the mutation at wave-start (analog to boss/stealth).
    this.currentModifier_ = this.rollModifier(this.currentWave_, isBossWave);
    if (this.currentModifier_) {
      this.bus.emit("wave.mutation.incoming", {
        wave: this.currentWave_,
        modifier: this.currentModifier_,
      });
    }

    // CR-H3 (LD-22.1-2): Telegraph stealth-containing waves so the player
    // knows to build a Signal tower. Scans spawn queue for any enemy type
    // whose config has `stealth: true`. Fires at wave.start (analog to
    // boss.wave.incoming) — Signal tower (Beacon-Node, cost 75) is
    // buildable, so even mid-wave warning is actionable.
    const stealthTypes = new Set<string>();
    for (const e of this.spawnQueue) {
      if (e.type === "__delay__") continue;
      const cfg = this.enemyData[e.type];
      if (cfg?.stealth) stealthTypes.add(e.type);
    }
    if (stealthTypes.size > 0) {
      this.bus.emit("wave.stealth.incoming", {
        wave: this.currentWave_,
        types: [...stealthTypes],
      });
    }

    // BL-156 / D-254 (S157) — the "NEW THREAT DETECTED" telegraph (the fairness
    // contract's invariant 1: telegraphed BEFORE it can hurt). The decision is
    // now owned by the ENEMY BRAIN: it scans the queue for a DATA-flagged new
    // archetype (`special.newThreat`) making its FIRST appearance this run and
    // emits `wave.newthreat.incoming` ONCE per mechanic — the exact relocation
    // of the old inline scan + the retired `NEW_THREAT_TYPES` allowlist.
    this.director.announceNewThreats(this.currentWave_, this.spawnQueue);

    // S60: Telegraph first shielded wave (once per run)
    if (this.currentWave_ >= SHIELD_START_WAVE && !this.shieldAnnounced_) {
      this.shieldAnnounced_ = true;
      this.bus.emit("wave.shielded.incoming", { wave: this.currentWave_ });
    }

    this.bus.emit("wave.started", { wave: this.currentWave_ });
  }

  update(dt: number): void {
    if (!this.waveActive_) return;

    let spawnedThisFrame = false;
    if (this.spawnIndex < this.spawnQueue.length) {
      this.spawnTimer -= dt;
      if (this.spawnTimer <= 0) {
        const next = this.spawnQueue[this.spawnIndex++]!;
        if (next.type !== "__delay__") {
          this.spawnEnemy(next.type);
        }
        this.spawnTimer = next.delay;
        spawnedThisFrame = true;
      }
    }

    this.tickEnemies(dt, spawnedThisFrame);
  }

  /** F2 / DD-14.7-5: tick enemy positions WITHOUT advancing spawn queue.
   *  Used when breach cinematic is active so existing enemies keep moving
   *  but no new enemies enter the wave. */
  updateEnemiesOnly(dt: number): void {
    if (!this.waveActive_) return;
    this.tickEnemies(dt, false);
  }

  private tickEnemies(dt: number, spawnedThisFrame: boolean): void {
    const enemies = this.enemyPool.active;
    const toRelease: Enemy[] = [];

    for (let i = enemies.length - 1; i >= 0; i--) {
      const enemy = enemies[i]!;

      // Boss add-spawning — checked BEFORE enemy.update so that crossing the
      // HP threshold in the same frame as the kill still triggers adds. Gate
      // on hp > 0 (not active) so that a fatal DoT tick in update() doesn't
      // suppress the spawn that the prior frame's damage earned. The trigger
      // is the ENEMY_BEHAVIORS `spawnAdds` onPreUpdate handler (BL-151, exact
      // relocation — same threshold gate, !addSpawned once-guard, isAdd spawn);
      // the call site (here, pre-update) and its order are unchanged.
      if (enemy.hp > 0 && enemy.special) {
        ENEMY_BEHAVIORS[enemy.special.type].onPreUpdate?.(
          enemy,
          this.behaviorCtx
        );
      }

      enemy.update(dt);

      // ── B7 STUCK-ENEMY WATCHDOG (spec `2026-time-warp-softlock`) ──────────
      // The softlock's second occurrence (build 1249, W28) showed an EMPTY
      // board with a wave that would not end and no new spawns — the signature
      // of `enemyPool.active.length` never reaching 0 because an enemy the
      // player cannot see is alive and unable to move.
      //
      // `PathRerouter` has no force-leak safety: its documented fallback is
      // "enemy keeps old path remainder", and it early-returns entirely when
      // the new path is empty. Placing a tower or firewall MID-WAVE is what
      // triggers a reroute — and the owner was building when it hung. So an
      // enemy can be left holding a stale route it can never finish.
      //
      // Rather than chase every stranding path, this makes the OUTCOME
      // impossible: an enemy that makes no progress at all — neither advancing
      // its waypoint nor moving a pixel — for STUCK_TIMEOUT_MS is removed.
      //
      // Removed WITHOUT a leak and WITHOUT a bounty, deliberately. Charging the
      // player a life for our own defect would be wrong, and paying a reward
      // would make stalling profitable. It simply stops existing so the wave
      // can end.
      if (enemy.active) {
        const moved =
          enemy.pathIndex !== enemy.lastProgressIndex ||
          enemy.x !== enemy.lastProgressX ||
          enemy.y !== enemy.lastProgressY;
        if (moved) {
          enemy.stalledMs = 0;
          enemy.lastProgressIndex = enemy.pathIndex;
          enemy.lastProgressX = enemy.x;
          enemy.lastProgressY = enemy.y;
        } else {
          enemy.stalledMs += dt;
          if (enemy.stalledMs >= STUCK_TIMEOUT_MS) {
            enemy.active = false;
            this.bus.emit("enemy.stuckRemoved", {
              enemy,
              wave: this.currentWave_,
              stalledMs: enemy.stalledMs,
            });
          }
        }
      }

      // Final boss phase transitions — checked AFTER enemy.update (the cascade
      // reads the post-move HP). The per-phase logic is the ENEMY_BEHAVIORS
      // `finalBoss` onPostUpdate handler (BL-151, exact relocation — same
      // phaseChange emit, per-phase adds, speed mults, P3 resistance null);
      // the call site (here, post-update) and its order are unchanged.
      if (enemy.active && enemy.special) {
        ENEMY_BEHAVIORS[enemy.special.type].onPostUpdate?.(
          enemy,
          this.behaviorCtx,
          dt
        );
      }

      if (!enemy.active) {
        if (enemy.reachedEnd) {
          this.leaksThisWave_++;
          this.bus.emit("enemy.reachedEnd", { enemy });
        } else if (enemy.hp <= 0) {
          this.bus.emit("enemy.killed", { enemy });
          // BL-175 / D-266 — virus's GOLD "PANDEMIC": an enemy carrying a GOLD-
          // tagged contagion bursts a spore cloud re-infecting nearby foes on
          // death. No-op (byte-identical) for any untagged corpse — no fixture
          // ever infects from a GOLD tower, so no contagion is ever gold-tagged.
          const spored = burstPandemicSpores(enemy, enemies);
          if (spored > 0) {
            this.bus.emit("virus.spread", {
              x: enemy.x,
              y: enemy.y,
              generation: 0,
            });
          }
          // BL-155 (S158) — the Splitter's death-spawn: on a KILL (not a leak)
          // the registry's onDeath fractures it into fast sub-enemies via the
          // `spawnAdd` seam. Only `splitter` registers one (else byte-identical).
          if (enemy.special) {
            ENEMY_BEHAVIORS[enemy.special.type].onDeath?.(
              enemy,
              this.behaviorCtx
            );
          }
          if (isBoss(enemy)) {
            this.bus.emit("boss.killed", {
              boss: enemy,
              wave: this.currentWave_,
              leaksThisWave: this.leaksThisWave_,
            });
            this.bus.emit("boss.defeated", {
              boss: enemy,
              wave: this.currentWave_,
            });
          }
        }
        toRelease.push(enemy);
      }
    }

    for (const enemy of toRelease) {
      this.enemyPool.release(enemy);
    }

    this.checkWaveCompletion(spawnedThisFrame, dt);
  }

  /**
   * GA-41 — "is this wave over?", callable WITHOUT ticking enemies.
   *
   * Extracted from {@link tickEnemies} so the freeze paths can still ask the
   * question. Previously this test lived only inside the enemy tick, and
   * `SimStep`'s freeze gate skips that tick entirely while time-warp is active
   * — so a wave whose last enemy died just before a warp could not complete
   * until the warp ended. That was harmless only while GA-40 kept the warp
   * under one frame; fixing GA-40 without this would have introduced a real
   * multi-second stall. Fix the two together, never GA-40 alone.
   *
   * Behaviour for the existing callers is unchanged: same condition, same
   * order, same event — `tickEnemies` simply delegates to it now.
   */
  checkWaveCompletion(spawnedThisFrame = false, dt = 0): void {
    if (!this.waveActive_) return;

    // ── B7 WAVE-LEVEL DEADLOCK BACKSTOP ──────────────────────────────────────
    // The stuck-enemy watchdog closes the `active.length === 0` clause. This
    // closes the other one: a wave whose spawn queue stops advancing. The
    // owner's second report was exactly that shape — an EMPTY board with no new
    // enemies and a wave that would not end.
    //
    // It deliberately watches OUTCOMES, not mechanisms. Every previous fix in
    // this class targeted a specific cause (GA-40 the timer unit, GA-41 the
    // completion call site, D-312 two freeze routes) and each time a different
    // cause reopened it. A fingerprint of "did anything at all change?" cannot
    // be bypassed by a mechanism nobody has thought of yet.
    //
    // This runs from `checkWaveCompletion` on purpose: it is the ONE call site
    // reached by all three of SimStep's branches — normal, cinematic-paused and
    // time-warp-frozen — so the counter keeps ticking even while the wave is
    // frozen, which is precisely when a deadlock would otherwise be invisible.
    if (dt > 0) {
      // Positions are included (rounded, so sub-pixel drift is not "progress")
      // because a wave can be alive and healthy with a stable spawnIndex and a
      // stable enemy count while enemies simply walk.
      let posSum = 0;
      for (const e of this.enemyPool.active) {
        posSum += Math.round(e.x) + Math.round(e.y) * 31 + e.pathIndex * 7;
      }
      const sig = `${this.spawnIndex}|${this.enemyPool.active.length}|${this.leaksThisWave_}|${posSum}`;
      if (sig === this.waveSignature_) {
        this.waveStallMs_ += dt;
        if (this.waveStallMs_ >= WAVE_STALL_TIMEOUT_MS) {
          // Force-resolve the WAVE, not the run. Ending the run on our own
          // defect would punish the player for a bug; completing the wave lets
          // them keep playing and loses nothing they earned.
          this.waveActive_ = false;
          this.bus.emit("wave.stallResolved", {
            wave: this.currentWave_,
            stalledMs: this.waveStallMs_,
            spawnIndex: this.spawnIndex,
            queueLength: this.spawnQueue.length,
            activeEnemies: this.enemyPool.active.length,
          });
          this.bus.emit("wave.completed", { wave: this.currentWave_ });
          return;
        }
      } else {
        this.waveSignature_ = sig;
        this.waveStallMs_ = 0;
      }
    }

    if (
      !spawnedThisFrame &&
      this.spawnIndex >= this.spawnQueue.length &&
      this.enemyPool.active.length === 0
    ) {
      this.waveActive_ = false;
      this.bus.emit("wave.completed", { wave: this.currentWave_ });
    }
  }

  /**
   * S141 / D-236 — Lever 1: the seeded mutation roll. Constraints (research §3 /
   * bible §4.1): never before {@link MUTATION_START_WAVE}, never on boss waves,
   * no back-to-back repeat, rate ramps across the acts (0.50 → 0.75 → 1.00).
   * Draws ONLY from the isolated `modifierRng` — the combat/draft stream is
   * untouched (the determinism dividend). No draw is consumed when the wave is
   * ineligible (so adding/removing eligible waves is the only thing that moves
   * the stream, keeping the re-baseline story legible).
   */
  private rollModifier(wave: number, isBossWave: boolean): WaveModifier | null {
    // modifierSeed 0 ⇒ mutations OFF (determinism-cold). Production + the sim
    // fixtures pass a real derived seed (rng.seed ^ MODIFIER_SEED_SALT, ≠ 0);
    // bare unit harnesses default to 0 so their HP/composition contracts stay
    // unmutated. (The 0-collision with a real run seed is astronomically rare.)
    if (this.modifierSeed === 0) return null;
    if (wave < MUTATION_START_WAVE || isBossWave) return null;
    if (this.modifierRng.next() >= mutationRate(wave)) return null;
    // Pick from the pool excluding the previous wave's modifier (no repeat).
    const pool = this.lastModifier_
      ? MODIFIER_POOL.filter((m) => m !== this.lastModifier_)
      : MODIFIER_POOL;
    const pick = pool[Math.floor(this.modifierRng.next() * pool.length)]!;
    this.lastModifier_ = pick;
    return pick;
  }

  /**
   * S141 / D-236 — apply the active wave mutation to a spawn config (mutates a
   * COPY). Scalar stats reuse the existing fields; `splitting` reuses the proven
   * boss-add `spawnAdds` path (threshold 0.5). Speed stays under the anti-cheese
   * 2.5× cap (CR-14). Never called for internally-spawned adds (no cascade).
   */
  private applyModifier(config: EnemyConfig, baseSpeed: number): EnemyConfig {
    const mod = this.currentModifier_;
    if (!mod) return config;
    // BL-152 (S154) → BL-170 (S163) — the per-act "new threats" are EXEMPT from
    // the ECHELON mutation. The exemption keys off the DATA flag `special.newThreat`
    // (not a hardcoded type list) so it can't silently drift again: every
    // threat-pack archetype already carries the flag — jammer + overseer, and (the
    // BL-170 fix) leech + regenerator + splitter — and any future threat added with
    // the one-line `newThreat` edit is exempt for free. WHY exempt: a `splitting`
    // roll overwrites their `special` (deleting the very mechanic that IS their
    // identity), and `veiled` cloaks an illegible credit-drainer / tower-suppressor
    // the player can't target — the unfair, illegible moment that breaks the BL-153
    // fairness contract (a ~W13 invisible Leech silently draining you). The mutation
    // ROLL is untouched (this only skips APPLICATION); `newThreat` already gates the
    // "NEW THREAT DETECTED" telegraph, so one flag marks "this enemy's identity IS
    // its special". Behaviour change → re-baselines the late-curve / breach fixtures
    // (leech/regenerator/splitter stop rolling splitting/veiled from ~W13 on).
    if (config.special?.newThreat) {
      return config;
    }
    switch (mod) {
      case "hardened":
        return {
          ...config,
          hp: Math.round(config.hp * 1.3),
          speed: config.speed * 0.9,
        };
      case "swift":
        return {
          ...config,
          hp: Math.round(config.hp * 0.9),
          speed: Math.min(config.speed * 1.3, baseSpeed * 2.5),
        };
      case "veiled":
        return { ...config, stealth: true };
      case "splitting":
        return {
          ...config,
          special: {
            type: "spawnAdds",
            addType: "swarm",
            spawnCount: 2,
            spawnThreshold: 0.5,
          },
        };
    }
  }

  /** GA-08 (Spec C+): parameterised so {@link previewComposition} can build a
   *  FUTURE wave's queue without touching run state. The default keeps every
   *  existing call site byte-identical — this is a signature change, not a
   *  behaviour change. */
  private buildSpawnQueue(
    waveOverride: number = this.currentWave_
  ): { type: string; delay: number }[] {
    const wave = waveOverride;

    // Final wave: final boss wave (P23-6: compressed from 45)
    if (wave === FINAL_WAVE) {
      return this.buildFinalBossQueue();
    }

    const template = this.getTemplate(wave);
    const queue: { type: string; delay: number }[] = [];

    // preDelay: pause before first spawn
    if (template.preDelay && template.preDelay > 0) {
      queue.push({ type: "__delay__", delay: template.preDelay });
    }

    for (let g = 0; g < template.groups.length; g++) {
      const group = template.groups[g]!;
      // groupDelay: pause between groups
      if (g > 0 && group.groupDelay && group.groupDelay > 0) {
        queue.push({ type: "__delay__", delay: group.groupDelay });
      }
      const countBonus =
        this.scaling.countBonusPerWave ?? this.scaling.countBonus ?? 1;
      let scaledCount = group.count + Math.floor((wave - 1) * countBonus * 0.5);
      // S-8 (Spec B, Act-I correctness slice) — cap a boss-type group's
      // SCALED count so its total `livesDamage` never exceeds
      // `bossLivesDamageCapFraction * GAME.INITIAL_HP` (default 100%) in a
      // single wave. `countBonusPerWave` above has no per-type exemption, so
      // without this a late-authored wave (and, unboundedly, Echo Mode —
      // this same path runs for both) can scale boss count past the entire
      // life budget (measured: W15 = 6 bosses × 5 = 30 = 120%; Echo Mode W36
      // = 11, W41 = 13). The cap fraction is the data lever
      // (`waves.json` scaling.bossLivesDamageCapFraction); only `final_boss`
      // (single hard-coded spawn in buildFinalBossQueue, never scaled here)
      // is unaffected.
      const groupConfig = this.enemyData[group.type];
      if (groupConfig && isBossConfig(groupConfig) && groupConfig.livesDamage) {
        const capFraction = this.scaling.bossLivesDamageCapFraction ?? 1;
        const maxBossCount = Math.floor(
          (GAME.INITIAL_HP * capFraction) / groupConfig.livesDamage
        );
        scaledCount = Math.min(scaledCount, maxBossCount);
      }
      for (let i = 0; i < scaledCount; i++) {
        queue.push({ type: group.type, delay: group.delay });
      }
    }

    // CR-20: Halve spawn delays for waves 1–10 to compress tutorial arc
    if (wave <= 10) {
      for (const entry of queue) {
        entry.delay = Math.round(entry.delay * 0.5);
      }
    }

    return queue;
  }

  private buildFinalBossQueue(): { type: string; delay: number }[] {
    const queue: { type: string; delay: number }[] = [];
    // Pre-delay before first spawn (matches template preDelay)
    queue.push({ type: "scout", delay: 2000 });
    // 9 more warm-up enemies
    for (let i = 0; i < 9; i++) {
      queue.push({ type: "scout", delay: 500 });
    }
    // Group delay before final boss (matches template groupDelay)
    queue.push({ type: "final_boss", delay: 3000 });
    return queue;
  }

  /** P23-6: Public access to wave template for boss-detection in handlers */
  getWaveTemplate(wave: number) {
    return this.getTemplate(wave);
  }

  /** GA-08 (Spec C+) — the SCALED composition of a future wave, for
   *  `intrusion_scanner`'s wave preview.
   *
   *  Deliberately built from {@link buildSpawnQueue}, not from the authored
   *  template: the template says `stealth_daemon x14` while the player will
   *  actually meet 18 at W16, and a preview that under-reports is worse than
   *  no preview. Read-only — it mutates no run state.
   *
   *  Returns [] past the final wave. */
  previewComposition(
    wave: number
  ): { type: string; name: string; count: number }[] {
    if (wave > FINAL_WAVE) return [];
    const counts = new Map<string, number>();
    for (const entry of this.buildSpawnQueue(wave)) {
      if (entry.type === "__delay__") continue;
      counts.set(entry.type, (counts.get(entry.type) ?? 0) + 1);
    }
    return [...counts.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([type, count]) => ({
        type,
        name: this.enemyData[type]?.name ?? type,
        count,
      }));
  }

  private getTemplate(wave: number) {
    const templates = this.waveData.templates;
    // Echo Mode (endless, wave > FINAL_WAVE) has no authored templates — it
    // rotates the late-game pool instead of falling through to the last
    // (boss) template. See getEndlessTemplate (BL-253).
    if (wave > FINAL_WAVE) {
      return this.getEndlessTemplate(wave);
    }
    // Find exact match by wave number
    const exact = templates.find((t: { wave: number }) => t.wave === wave);
    if (exact) return exact;
    // Fallback for an authored-range gap (waves.json ships one template per
    // wave 1..FINAL_WAVE, so this should not fire): use the last template.
    return templates[templates.length - 1]!;
  }

  /**
   * BL-253 (S192-F10): Echo Mode composition. Endless waves used to fall
   * through getTemplate's fallback onto the LAST template — the wave-35
   * final-boss wave — and the per-wave count scaling then multiplied the
   * `final_boss` group (~11 AXIOM-77 at W36, growing every wave). Instead,
   * endless deterministically rotates the authored late-game pool (the
   * ENDLESS.TEMPLATE_POOL_FIRST_WAVE..34 non-boss waves); HP scaling
   * (getHpMultiplier) and the count bonus keep climbing with the real wave
   * number, so Echo Mode escalates without boss duplication. Pure function of
   * the wave number — deterministic and save-free by construction.
   */
  private getEndlessTemplate(wave: number) {
    const pool = this.waveData.templates.filter(
      (t) =>
        t.wave >= ENDLESS.TEMPLATE_POOL_FIRST_WAVE &&
        t.wave < FINAL_WAVE &&
        !t.groups.some((g) => g.type === "final_boss")
    );
    if (pool.length === 0) {
      // Degenerate data (no authored late-game pool): any non-boss template
      // beats duplicating the boss wave.
      const nonBoss = this.waveData.templates.filter(
        (t) => !t.groups.some((g) => g.type === "final_boss")
      );
      const fallback = nonBoss[nonBoss.length - 1];
      if (fallback) return fallback;
      return this.waveData.templates[this.waveData.templates.length - 1]!;
    }
    return pool[(wave - FINAL_WAVE - 1) % pool.length]!;
  }

  private getHpMultiplier(wave: number): number {
    // P23-6: flat per-wave multiplier (new format)
    if (this.scaling.hpMultiplierPerWave) {
      return Math.pow(this.scaling.hpMultiplierPerWave, wave - 1);
    }
    // Legacy phased format
    let mult = 1;
    let prevEnd = 1;
    const phases = this.scaling.hpMultiplierPhases ?? [];
    for (const phase of phases) {
      if (wave <= prevEnd) break;
      const stepsInPhase = Math.min(wave, phase.upToWave) - prevEnd;
      if (stepsInPhase > 0) {
        mult *= Math.pow(phase.multiplier, stepsInPhase);
      }
      prevEnd = phase.upToWave;
    }
    // GA-38 (P6, D-305): the shipped data ends its authored phase list at a
    // fixed upToWave, which used to leave HP flat forever beyond it — a
    // permanent plateau. Continue compounding at the LAST authored phase's
    // multiplier past its upToWave so Echo Mode keeps ramping "smoothly and
    // continuously, with no ... plateau". This branch is only reachable when
    // `wave` exceeds every phase's upToWave, i.e. the loop above always ran
    // to completion (never broke early) and `prevEnd` already equals the
    // last phase's upToWave — so W1..200 (the shipped arc) is unchanged.
    const lastPhase = phases[phases.length - 1];
    if (lastPhase && wave > lastPhase.upToWave) {
      mult *= Math.pow(lastPhase.multiplier, wave - lastPhase.upToWave);
    }
    return mult;
  }

  private spawnEnemy(type: string, isAdd = false): void {
    if (this.pathPixels_.length === 0) return;

    const baseConfig = this.enemyData[type];
    if (!baseConfig) return;

    const wave = this.currentWave_;
    // Chain step A (2026-08-18): the threat profile multiplies BOTH axes here,
    // at the one place every spawned enemy's stats are computed. HP before the
    // round (never a fractional HP), speed INSIDE the 2.5× cap — the cap is
    // projectile physics (CR-14), and a harder threat level makes enemies
    // faster but never faster than the physics tolerates; fallout simply
    // reaches the ceiling earlier in the wave curve. Containment's 1.00/1.00
    // multiplies exactly (IEEE 754), keeping the golden masters byte-stable.
    let scaledConfig: EnemyConfig = {
      ...baseConfig,
      hp: Math.round(
        baseConfig.hp *
          this.getHpMultiplier(wave) *
          this.threatProfile_.enemyHpMult
      ),
      // CR-14 (C3): Cap speed at 2.5× base to prevent outrunning projectiles
      speed: Math.min(
        baseConfig.speed *
          Math.pow(
            this.scaling.speedMultiplierPerWave ??
              this.scaling.speedMultiplier ??
              1.02,
            wave - 1
          ) *
          this.threatProfile_.enemySpeedMult,
        baseConfig.speed * 2.5
      ),
    };

    // S141 / D-236 — apply the wave mutation to primary spawns only; adds spawned
    // by a splitting/boss enemy do NOT re-mutate (no split cascade).
    if (!isAdd && this.currentModifier_) {
      scaledConfig = this.applyModifier(scaledConfig, baseConfig.speed);
    }

    // CR-5 (P22) + CR-H6 (P22.1): iterate id-paired path entries from
    // PathRegistry so each spawned enemy knows which path it's bound to
    // (LD-22-12 + LD-22.1-4). Bosses still primary-only.
    // Composition: primary always exists (from registry if registered,
    // else from this.pathPixels_ fallback for tests). Non-primary entries
    // come exclusively from registry (breach activations).
    const bossType = isBossConfig(baseConfig);
    const registryEntries = this.pathRegistry_?.getAllPathEntries() ?? [];
    const registryPrimary = registryEntries.find((e) => e.id === "primary");
    const allEntries: Array<{ id: string; pixels: Vec2[] }> = [
      {
        id: "primary",
        pixels: registryPrimary?.pixels ?? this.pathPixels_,
      },
      ...registryEntries.filter((e) => e.id !== "primary"),
    ];
    const entries = bossType
      ? allEntries.filter((e) => e.id === "primary")
      : allEntries;

    // AC-10 / O-5 branch (c) — ROUTING INSTEAD OF DUPLICATION.
    //
    // The loop below still emits exactly `entries.length` enemies, so the
    // per-wave spawn count and the delivered Σ-HP are INVARIANT under this
    // lever at every fraction. That invariance is the point: AC-10 pairs the
    // routing change with a "delivered Σ-HP did not fall" assertion,
    // because a routing lever that shed volume would make the game easier
    // while claiming to harden it.
    //
    // What changes is WHICH lane each of those spawns emits from. Before,
    // slot i always took entries[i], so every breach lane received an exact
    // MIRROR of the whole non-boss composition — a pure volume multiplier
    // (the O-5 "more of the same" complaint, and the opposite of
    // CHALLENGE_DESIGN.md:230-247's "flanking sub-group"). Now each slot is
    // re-drawn onto a seeded lane with probability
    // `scaling.breachRoutedSpawnFraction`, so lanes carry different, uneven,
    // seed-varying shares of the wave instead of identical copies.
    //
    // At fraction 0 no draw is taken and slot i keeps entries[i] — the
    // pre-lever behaviour, byte-identical. The draws come from the run's
    // EXISTING isolated breach stream (OQ-3): no new RNG stream and no new
    // determinism surface, so runs stay exactly seed-reproducible.
    const routedFraction = this.scaling.breachRoutedSpawnFraction ?? 0;
    const canRoute = !bossType && routedFraction > 0 && entries.length > 1;

    for (let slot = 0; slot < entries.length; slot++) {
      let entry = entries[slot]!;
      if (canRoute && this.breachRoutingRng.next() < routedFraction) {
        const pick = entries[this.breachRoutingRng.int(entries.length)];
        if (pick && pick.pixels.length > 0) entry = pick;
      }
      if (entry.pixels.length === 0) continue;
      const enemy = this.enemyPool.acquire();
      enemy.init(scaledConfig, entry.pixels, 0);
      enemy.spawnPathId = entry.id; // CR-H6: tag enemy with its path origin
      // S60: roll shield for non-boss enemies on eligible waves
      applyShield(enemy, wave, scaledConfig.hp, this.shieldRng, bossType);
      this.bus.emit("enemy.spawned", { enemy });
      if (isBoss(enemy)) {
        this.bus.emit("boss.spawned", { boss: enemy, wave: this.currentWave_ });
      }
    }
  }
}

/** Boss detection from config (pre-spawn). Mirrors isBoss() but works on
 *  EnemyConfig so spawnEnemy can branch (off-path filter + shield skip) before
 *  instantiation. Keyed off `special.type` only — see isBoss() for the why. */
function isBossConfig(config: EnemyConfig): boolean {
  const t = config.special?.type;
  return t === "finalBoss" || t === "spawnAdds";
}

/** Boss detection (BL-163, S163): a boss is EXACTLY an enemy whose `special.type`
 *  is `finalBoss` (AXIOM-77) or `spawnAdds` (Core Breaker) — the two real,
 *  data-defined bosses. The retired `maxHp >= 500` heuristic mis-classed the
 *  anchor `cipher_breaker` (base hp exactly 500, no boss special) as a boss,
 *  leaking it into the shield-skip / primary-path-only-spawn / `boss.*`-event /
 *  `bossKilledClean` paths. Keying off `special.type` is the system-wide fix that
 *  the BL-160 audio/music guards anticipated (they already re-check `special.type`,
 *  now belt-and-suspenders). No data-driven boss lacks a `special.type`, so the
 *  real bosses are unaffected; `cipher_breaker` correctly becomes a normal anchor
 *  (standard shield schedule, off-path spawn, no boss events). */
function isBoss(enemy: Enemy): boolean {
  const t = enemy.special?.type;
  return t === "finalBoss" || t === "spawnAdds";
}
